/* eslint-disable @typescript-eslint/no-use-before-define */
import {
    Action,
    ActionReducerMap,
    createFeatureSelector,
    createSelector,
    StoreModule,
} from '@ngrx/store';
import { find } from 'lodash-es';
import {
    AddSponsor,
    ClubAction,
    RemoveSponsor,
    UpdateSponsor,
} from 'src/app/state/club/club.actions';

import { Place, PremiumPlace } from '@polaris-customerweb/sharedcomponents.map';
import {
    PlaceAction,
    SetActivePointOfInterest,
    SetActivePremiumPoi,
    SetPlacesInView,
} from './point-of-interest.actions';

export interface PlaceState {
    activePointOfInterest?: Place;
    activePremiumPoi?: PremiumPlace;
    placesInView: (Place | PremiumPlace)[];
}
const reducerMap: ActionReducerMap<PlaceState> = {
    activePointOfInterest: activePointOfInterestReducer,
    activePremiumPoi: activePremiumPoiReducer,
    placesInView: placesInViewReducer,
};

export const selectPlacesState =
    createFeatureSelector<PlaceState>('placeState');
export const selectActivePointOfInterest = createSelector(
    selectPlacesState,
    (state) => state.activePointOfInterest
);
export const selectActivePremiumPoi = createSelector(
    selectPlacesState,
    (state) => state.activePremiumPoi
);
export const selectAllPlacesInView = createSelector(
    selectPlacesState,
    (state) => state.placesInView
);
export const selectPremiumPointsOfInterestInView = createSelector(
    selectPlacesState,
    (state) => {
        return state.placesInView.filter(
            (place) => place instanceof PremiumPlace
        );
    }
);
export const selectStandardPlacesInView = createSelector(
    selectPlacesState,
    (state) => {
        return state.placesInView.filter(
            (place) => !(place instanceof PremiumPlace)
        );
    }
);

export const placeStoreModule = StoreModule.forFeature(
    'placeState',
    reducerMap
);

function activePointOfInterestReducer(
    state: PremiumPlace,
    action: Action
): Place | undefined {
    switch (action.type) {
        case PlaceAction.setActivePointOfInterest:
            return (action as SetActivePointOfInterest).pointOfInterest;
        default:
            return state;
    }
}

function activePremiumPoiReducer(
    state: PremiumPlace | undefined,
    action: Action
): PremiumPlace | undefined {
    switch (action.type) {
        case PlaceAction.setActivePremiumPlace:
            return (action as SetActivePremiumPoi).place;
        default:
            return state;
    }
}

function placesInViewReducer(
    state: (Place | PremiumPlace)[] = [],
    action: Action
): (Place | PremiumPlace)[] {
    let otherPlaces: (Place | PremiumPlace)[];
    let foundPremiumPlace: PremiumPlace | undefined;

    switch (action.type) {
        case PlaceAction.setPlacesInView:
            return (action as SetPlacesInView).places;

        case ClubAction.addSponsor:
        case ClubAction.updateSponsor:
            // replace existing premium place (if any) with copy from sponsor
            otherPlaces = state.filter(
                (place) =>
                    place.factualId !==
                    (action as AddSponsor | UpdateSponsor).sponsor.factualId
            );
            return [
                ...otherPlaces,
                (action as AddSponsor | UpdateSponsor).sponsor.toPremiumPlace(),
            ];

        case ClubAction.removeSponsor:
            // downgrade premium place matching sponsor
            foundPremiumPlace = find(state, {
                factualId: (action as RemoveSponsor).sponsorId,
            }) as PremiumPlace | undefined;
            if (!foundPremiumPlace) {
                return state;
            }
            otherPlaces = state.filter(
                (place) =>
                    place.factualId !== (action as RemoveSponsor).sponsorId
            );
            return [...otherPlaces, foundPremiumPlace.toPlace()];

        default:
            return state;
    }
}
